import { useState } from "react";
import { useParams } from "react-router-dom";
import { CircleCheck, Loader2 } from "lucide-react";
import { SupportLink } from "@/components/common/SupportLink";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { INTERVIEWER } from "@/lib/roles";
import { sendInterviewFeedback } from "@/services/interviewApi";

/**
 * A hiring signal instead of stars (Alex, 2026-10-26). The platform stores
 * `rating` as 1 to 5, so each step keeps its number.
 */
export const SIGNALS = [
  { rating: 1, label: "Strong no" },
  { rating: 2, label: "No" },
  { rating: 3, label: "Unsure" },
  { rating: 4, label: "Yes" },
  { rating: 5, label: "Strong yes" },
];

const sentKey = roomId => `feedback-sent:${roomId}`;

/** Whether feedback for this room was sent earlier in this tab (rejoins). */
function wasSent(roomId) {
  try {
    return window.sessionStorage.getItem(sentKey(roomId)) === "1";
  } catch {
    return false;
  }
}

function rememberSent(roomId) {
  try {
    window.sessionStorage.setItem(sentKey(roomId), "1");
  } catch {
    // Storage blocked: the form shows again after a rejoin.
  }
}

const HiringSignal = ({ value, onChange, invalid }) => (
  <ToggleGroup
    type="single"
    variant="outline"
    value={value ? String(value) : ""}
    onValueChange={next => next && onChange(Number(next))}
    aria-label="Would you move them forward?"
    aria-invalid={invalid || undefined}
    className="grid w-full grid-cols-5"
  >
    {SIGNALS.map(({ rating, label }) => (
      <ToggleGroupItem
        key={rating}
        value={String(rating)}
        data-testid={`feedback_btn_${rating}`}
        className="h-auto min-h-10 px-1 py-2 text-xs whitespace-normal data-[state=on]:bg-primary data-[state=on]:text-primary-foreground sm:text-sm"
      >
        {label}
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);

/**
 * The interviewer's verdict on the candidate, inside the leave page's card,
 * posted to the platform, which stores it on the interview and notifies the
 * candidate's team.
 */
const FeedbackForm = ({ interviewInfo }) => {
  const { roomId, role } = useParams();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState({});
  const [state, setState] = useState(() =>
    wasSent(roomId) ? "sent" : "editing"
  );

  const name = interviewInfo?.profile?.name || "the candidate";
  const team = interviewInfo?.profile?.team_name || `${name}'s team`;

  const submit = async event => {
    event.preventDefault();
    const nextErrors = {};
    if (rating < 1) {
      nextErrors.rating = "Choose an answer, from Strong no to Strong yes.";
    }
    if (!comment.trim()) {
      nextErrors.comment = "Add a comment about the candidate.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      return;
    }
    setState("sending");
    try {
      await sendInterviewFeedback(roomId, {
        rating,
        comment,
        respondent: role === INTERVIEWER ? "client" : "developer",
      });
      rememberSent(roomId);
      setState("sent");
    } catch (error) {
      setState("failed");
    }
  };

  if (state === "sent") {
    return (
      <div
        className="flex items-center gap-3"
        data-testid="feedback_form"
        role="status"
      >
        <CircleCheck className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div>
          <p className="text-sm font-medium">Thanks for your feedback</p>
          <p className="text-sm text-muted-foreground">
            We shared it with {team}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4"
      noValidate
      data-testid="feedback_form"
    >
      <div>
        <h2 className="text-base font-semibold">How did {name} do?</h2>
        <p className="text-sm text-muted-foreground">
          Your answer and comment are shared with {team}.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-xs text-muted-foreground">
          Would you move them forward?
        </Label>
        <HiringSignal
          value={rating}
          onChange={value => {
            setRating(value);
            setErrors(current => ({ ...current, rating: undefined }));
          }}
          invalid={Boolean(errors.rating)}
        />
        {errors.rating ? (
          <p className="text-sm text-destructive" role="alert">
            {errors.rating}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <Label
          htmlFor="feedback-comment"
          className="text-xs text-muted-foreground"
        >
          Comment
        </Label>
        <Textarea
          id="feedback-comment"
          rows={3}
          placeholder="What went well, and what could be better?"
          value={comment}
          aria-invalid={Boolean(errors.comment) || undefined}
          onChange={event => {
            setComment(event.target.value);
            setErrors(current => ({ ...current, comment: undefined }));
          }}
        />
        {errors.comment ? (
          <p className="text-sm text-destructive" role="alert">
            {errors.comment}
          </p>
        ) : null}
      </div>
      {state === "failed" ? (
        <p className="text-sm text-destructive" role="alert">
          There was an error sending your feedback. Please try again, or{" "}
          <SupportLink /> if the problem persists.
        </p>
      ) : null}
      <Button
        type="submit"
        className="self-end"
        disabled={state === "sending"}
        data-testid="send_feedback_btn"
      >
        {state === "sending" ? <Loader2 className="animate-spin" /> : null}
        Send feedback
      </Button>
    </form>
  );
};

export default FeedbackForm;
